import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { MerchReportService } from '../../services/merchReport.service';
import { ItemService } from '../../services/item.service';
import { IMaterialRequirement } from '../../interfaces/garments/materialRequirement.interface';
import { Item } from '../../interfaces/item.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { StockReservationService } from '../../services/stockReservation.service';
import { ReservationStatus, ShortfallRequisitionResult, StockReservation } from '../../interfaces/stockReservation.interface';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface MaterialRequirementViewProps {
    buyerOrderId: string;
}

type NettingAction = 'reserve' | 'requisition' | { release: StockReservation };

/** Quantities come back with three decimals; show them without trailing zeros. */
const fmt = (n: number | null | undefined) => (n == null ? '0' : String(Number(n)));

/**
 * Material requirement view for a buyer order (garments-merchandising T7, gap row
 * 11.4) — one row per BOM item, aggregated across every style on the order. Fetched from
 * `MerchandisingReportController#getMaterialRequirement`.
 *
 * Buying-house D2: each row is netted against what was already issued to the order, what is held for it and what is
 * on order, so the Shortfall column is what still has to be found. Two actions act on that shortfall (hold it from
 * free stock, or raise one requisition for it), and the order's reservations are listed below with a Release button.
 */
export function MaterialRequirementView({ buyerOrderId }: MaterialRequirementViewProps) {
    const toast = useToast();

    const [requirement, setRequirement] = useState<IMaterialRequirement | null>(null);
    const [itemLookup, setItemLookup] = useState<Record<string, Item>>({});
    const [loading, setLoading] = useState(true);
    const [reservations, setReservations] = useState<StockReservation[]>([]);
    const [confirm, setConfirm] = useState<NettingAction | null>(null);
    const [busy, setBusy] = useState(false);
    const [statusFilter, setStatusFilter] = useState<ReservationStatus | ''>('ACTIVE');
    const [skippedNotice, setSkippedNotice] = useState<ShortfallRequisitionResult['skipped']>([]);
    const canViewReservations = usePermission(PERMISSIONS.STOCK_RESERVATION_VIEW);
    const canManageReservations = usePermission(PERMISSIONS.STOCK_RESERVATION_MANAGE);

    const loadReservations = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !canViewReservations) return;
        try {
            const page = await StockReservationService.list(institutionId, { buyerOrderId, status: statusFilter }, 0, 100);
            setReservations(page.content);
        } catch (e) {
            toast.showApiError(e, 'Failed to load stock reservations');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId, canViewReservations, statusFilter]);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setLoading(true);
        // The item names are a cosmetic lookup that a merchandiser/commercial role may not be
        // allowed to read (403) -- load them independently and silently so the requirement itself
        // still renders, and never raise the global error toast for them.
        const itemsPromise = ItemService.getItemsByInstitution(0, 500)
            .then(items => Object.fromEntries((items.content || []).map(i => [i.id as string, i])))
            .catch((): Record<string, Item> => ({}));
        try {
            setRequirement(await MerchReportService.getMaterialRequirement(institutionId, buyerOrderId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load material requirement');
        } finally {
            setLoading(false);
        }
        setItemLookup(await itemsPromise);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyerOrderId]);

    useEffect(() => {
        loadReservations();
    }, [loadReservations]);

    const hasShortfall = !!requirement?.lines.some(l => Number(l.shortfallQty) > 0);

    const runConfirmed = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !confirm) return;
        try {
            setBusy(true);
            if (confirm === 'reserve') {
                const r = await StockReservationService.reserveShortfall(institutionId, buyerOrderId);
                toast.showSuccess(`Held from stock: ${r.itemsFullyCovered} item(s) fully, ${r.itemsPartlyCovered} partly, `
                    + `${r.itemsNotCovered} not covered`);
            } else if (confirm === 'requisition') {
                const r = await StockReservationService.raiseShortfallRequisition(institutionId, buyerOrderId);
                setSkippedNotice(r.skipped ?? []);
                if (r.requisitionId) {
                    toast.showSuccess(`Requisition ${r.refNo ?? ''} raised for ${r.lineCount} item(s)`.replace('  ', ' '));
                } else {
                    toast.showError('No requisition was raised: every short item is a kit (see the note below).');
                }
            } else {
                await StockReservationService.release(institutionId, confirm.release.id);
                toast.showSuccess('Reservation released');
            }
            setConfirm(null);
            await load();
            await loadReservations();
        } catch (e) {
            toast.showApiError(e, 'The action failed');
        } finally {
            setBusy(false);
        }
    };

    const confirmText = (): string => {
        if (confirm === 'reserve') {
            return 'This holds free stock for this order, item by item, up to the shortfall (largest store first). Nothing leaves the store; other orders and manual issues can no longer take the held quantity.';
        }
        if (confirm === 'requisition') {
            return 'This raises one requisition tagged with this order for every short item, rounded up to whole units. Kit items are left out (list them below after). It still goes through the normal approval.';
        }
        if (confirm && typeof confirm === 'object') {
            const r = confirm.release;
            return `Release ${fmt(r.openQty)} ${r.unit ?? ''} of ${r.itemName ?? 'this item'} held in ${r.storeName ?? 'the store'}? It becomes free stock again.`;
        }
        return '';
    };

    const itemDisplay = (itemId: string) => {
        const item = itemLookup[itemId];
        return item ? `${item.itemName} (${item.itemCode})` : 'Item unavailable';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Material Requirement</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {canManageReservations && (
                        <>
                            <Button size="small" variant="outlined" data-help="merchandising.requirement.reserveShortfall"
                                data-testid="reserve-shortfall-btn" disabled={loading || !hasShortfall}
                                onClick={() => setConfirm('reserve')}>Reserve shortfall from stock</Button>
                            <Button size="small" variant="outlined" data-help="merchandising.requirement.raiseRequisition"
                                data-testid="raise-requisition-btn" disabled={loading || !hasShortfall}
                                onClick={() => setConfirm('requisition')}>Raise requisition for shortfall</Button>
                        </>
                    )}
                    <Button size="small" data-help="merchandising.requirement.refresh" onClick={load} disabled={loading}>Refresh</Button>
                </Box>
            </Box>
            {skippedNotice && skippedNotice.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setSkippedNotice([])} data-testid="skipped-kits-alert">
                    Left out of the requisition because a kit holds no stock. Request its component items instead:{' '}
                    {skippedNotice.map(k => `${k.itemName ?? 'Item'}${k.itemCode ? ` (${k.itemCode})` : ''}`).join(', ')}.
                </Alert>
            )}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} variant="outlined" data-testid="material-requirement-table" sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 800 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.requirement.col.item">Item</TableCell>
                                <TableCell data-help="merchandising.requirement.col.materialType">Material Type</TableCell>
                                <TableCell data-help="merchandising.requirement.col.uom">UoM</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.requiredQty">Required Qty</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.issuedQty">Issued</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.reservedQty">Reserved</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.onOrderQty">On order</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.openRequisitionQty">Requested</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.shortfallQty">Shortfall</TableCell>
                                <TableCell align="right" data-help="merchandising.requirement.col.freeStockQty">Free stock</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(!requirement || requirement.lines.length === 0) && (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        <Typography color="text.secondary" sx={{ py: 4 }}>
                                            No material requirement for this order.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {requirement?.lines.map((line, index) => (
                                <TableRow key={`${line.itemId}-${index}`} data-testid={`requirement-row-${line.itemId}`}>
                                    <TableCell>{itemDisplay(line.itemId)}</TableCell>
                                    <TableCell>{enumLabel(line.materialType)}</TableCell>
                                    <TableCell>{line.uom}</TableCell>
                                    <TableCell align="right">{line.requiredQty}</TableCell>
                                    <TableCell align="right">{fmt(line.issuedQty)}</TableCell>
                                    <TableCell align="right">{fmt(line.reservedOpenQty)}</TableCell>
                                    <TableCell align="right">{fmt(line.onOrderQty)}</TableCell>
                                    <TableCell align="right">{fmt(line.openRequisitionQty)}</TableCell>
                                    <TableCell align="right" sx={Number(line.shortfallQty) > 0 ? { color: 'error.main', fontWeight: 'bold' } : undefined}
                                        data-testid={`shortfall-${line.itemId}`}>{fmt(line.shortfallQty)}</TableCell>
                                    <TableCell align="right">{fmt(line.freeStockQty)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {canViewReservations && (
                <Box sx={{ mt: 3 }} data-help-section="merchandising.requirement.reservations">
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ flex: '1 1 200px' }}>Stock reserved for this order</Typography>
                        <TextField select size="small" label="Status" value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value as ReservationStatus | '')}
                            sx={{ flex: '1 1 140px', maxWidth: 200 }} data-help="merchandising.requirement.reservations.filter.status"
                            slotProps={{ htmlInput: { 'data-testid': 'reservation-status-filter' } }}>
                            <MenuItem value="ACTIVE">Active</MenuItem>
                            <MenuItem value="CONSUMED">Used</MenuItem>
                            <MenuItem value="RELEASED">Released</MenuItem>
                            <MenuItem value="">All</MenuItem>
                        </TextField>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 560 }} data-testid="order-reservations-table">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="merchandising.requirement.reservations.col.item">Item</TableCell>
                                    <TableCell data-help="merchandising.requirement.reservations.col.store">Store</TableCell>
                                    <TableCell align="right" data-help="merchandising.requirement.reservations.col.reserved">Reserved</TableCell>
                                    <TableCell align="right" data-help="merchandising.requirement.reservations.col.used">Used</TableCell>
                                    <TableCell align="right" data-help="merchandising.requirement.reservations.col.held">Still held</TableCell>
                                    <TableCell data-help="merchandising.requirement.reservations.col.status">Status</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reservations.length === 0 && (
                                    <TableRow><TableCell colSpan={7} align="center">Nothing matches this filter for this order.</TableCell></TableRow>
                                )}
                                {reservations.map(r => (
                                    <TableRow key={r.id} data-testid={`reservation-row-${r.id}`}>
                                        <TableCell>{r.itemName ?? 'Item unavailable'}{r.itemCode ? ` (${r.itemCode})` : ''}</TableCell>
                                        <TableCell>{r.storeName ?? '-'}</TableCell>
                                        <TableCell align="right">{fmt(r.reservedQty)}</TableCell>
                                        <TableCell align="right">{fmt(r.consumedQty)}</TableCell>
                                        <TableCell align="right">{fmt(r.openQty)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={r.status === 'ACTIVE' ? 'Active' : r.status === 'CONSUMED' ? 'Used' : 'Released'}
                                                color={r.status === 'ACTIVE' ? 'warning' : r.status === 'CONSUMED' ? 'success' : 'default'} />
                                        </TableCell>
                                        <TableCell>
                                            {canManageReservations && r.status === 'ACTIVE' && (
                                                <Button size="small" color="warning" data-help="merchandising.requirement.releaseReservation"
                                                    onClick={() => setConfirm({ release: r })}>Release</Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            <Dialog open={!!confirm} onClose={() => !busy && setConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {confirm === 'reserve' ? 'Reserve shortfall from stock'
                        : confirm === 'requisition' ? 'Raise requisition for shortfall' : 'Release reservation'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>{confirmText()}</DialogContentText>
                    {(confirm === 'reserve' || confirm === 'requisition') && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Quantities are compared in the item&apos;s stock unit, so the unit on the style&apos;s material list must be the same as the item&apos;s stock unit.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(null)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" onClick={runConfirmed} disabled={busy} data-testid="netting-confirm">Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MaterialRequirementView;
